
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  provide,
  reactive,
  ref,
} from "vue";
import {
  fetchDefaultSelection,
  listTypeFields,
  parseSelection,
  TypeFields,
} from "@/flow/application/graphqlSchemaApi";
import SelectionSetTreeNode from "./SelectionSetTreeNode.vue";
import {
  fieldSegment,
  fragmentSegment,
  hasDescendant,
  hydrate,
  pathKey,
  removeSubtree,
  serialize,
  TYPENAME,
} from "./selectionModel";

// Remembers each panel's expanded/collapsed state for the lifetime of the editor session (keyed by
// connector + action) so reopening a step doesn't re-collapse a panel a power user just opened.
const sessionPanelState = new Map<string, boolean>();

export default defineComponent({
  name: "SelectionSetTree",
  components: { SelectionSetTreeNode },
  props: {
    connectorId: { type: String, required: true },
    revision: {
      type: [Number, String] as PropType<number | string>,
      required: true,
    },
    rootType: { type: String, required: true },
    operation: { type: String, required: true },
    field: { type: String, required: true },
    modelValue: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "fallback"],
  setup(props: any, { emit }) {
    // Static context from the schema node; captured with concrete types for the API client.
    const connectorId = props.connectorId as string;
    const revision = props.revision as string | number;
    const operation = props.operation as string;
    const actionField = props.field as string;
    const rootTypeName = props.rootType as string;
    // Path-keyed selection, decoupled from the render tree (design section 7.5).
    const model = reactive({ paths: new Set<string>() });
    // Per-session schema cache: a type is fetched at most once (design section 7.3).
    const schemaCache = reactive(new Map<string, TypeFields>());
    const inFlight = new Map<string, Promise<TypeFields | null>>();
    const hydrating = ref(false);

    // The single "(all)" root node (design section 3). Its path is [] and its children — fetched
    // lazily only when it is expanded — are childrenMeta(rootType). Checking it selects all loaded
    // scalar descendants; unchecking removes the whole subtree (see toggle/removeSubtree).
    const rootMeta = {
      name: "(all)",
      type: rootTypeName,
      kind: "OBJECT",
      expandable: true,
      hasArgs: false,
      requiredArgs: false,
      deprecated: false,
      list: false,
      isRoot: true,
    };

    const panelStateKey = `${connectorId}:${operation}:${actionField}`;
    const panelOpen = ref(sessionPanelState.get(panelStateKey) ?? false);
    function togglePanel(): void {
      panelOpen.value = !panelOpen.value;
      sessionPanelState.set(panelStateKey, panelOpen.value);
    }

    function schemaFor(typeName: string): TypeFields | undefined {
      return schemaCache.get(typeName);
    }

    async function loadType(typeName: string): Promise<void> {
      if (schemaCache.has(typeName)) return;
      let promise = inFlight.get(typeName);
      if (!promise) {
        promise = listTypeFields(connectorId, revision, typeName);
        inFlight.set(typeName, promise);
      }
      const result = await promise;
      inFlight.delete(typeName);
      if (result) schemaCache.set(typeName, result);
    }

    function childrenMeta(typeName: string): any[] {
      const schema = schemaCache.get(typeName);
      if (!schema) return [];
      const metas: any[] = schema.fields.map((f) => ({ ...f }));
      if (schema.kind === "UNION" || schema.kind === "INTERFACE") {
        (schema.members || []).forEach((member) =>
          metas.push({
            name: `... on ${member}`,
            onType: member,
            type: member,
            kind: "FRAGMENT",
            expandable: true,
            hasArgs: false,
            requiredArgs: false,
            deprecated: false,
            list: false,
          })
        );
      }
      return metas;
    }

    function fullyChecked(segments: string[], meta: any): boolean {
      if (!meta.expandable) {
        return model.paths.has(pathKey(segments));
      }
      const typeName = meta.onType || meta.type;
      if (!schemaCache.has(typeName)) return false;
      // Deprecated / required-arg children are excluded from "select all" (see toggle), so they
      // must not count toward "fully checked" either — otherwise such a parent could never show as
      // checked, only indeterminate.
      const kids = childrenMeta(typeName).filter(
        (kid) => !kid.requiredArgs && !kid.deprecated
      );
      if (!kids.length) return model.paths.has(pathKey(segments));
      return kids.every((kid) =>
        fullyChecked([...segments, segmentOf(kid)], kid)
      );
    }

    function stateOf(
      segments: string[],
      meta: any
    ): { checked: boolean; indeterminate: boolean } {
      if (!meta.expandable) {
        return {
          checked: model.paths.has(pathKey(segments)),
          indeterminate: false,
        };
      }
      const checked = fullyChecked(segments, meta);
      const present =
        model.paths.has(pathKey(segments)) ||
        hasDescendant(model.paths, segments);
      return { checked, indeterminate: !checked && present };
    }

    function toggle(segments: string[], meta: any, checked: boolean): void {
      if (!checked) {
        removeSubtree(model.paths, segments);
      } else if (!meta.expandable) {
        model.paths.add(pathKey(segments));
      } else {
        model.paths.add(pathKey(segments));
        const typeName = meta.onType || meta.type;
        const schema = schemaCache.get(typeName);
        let addedScalar = false;
        if (schema) {
          schema.fields.forEach((f) => {
            // Deprecated and required-arg fields are never bulk-selected: they can produce failing
            // requests (deprecated) or invalid ones (required args), so a parent/(all) check must
            // not sneak them in. They stay reachable only via the Raw action.
            if (!f.expandable && !f.requiredArgs && !f.deprecated) {
              model.paths.add(pathKey([...segments, fieldSegment(f.name)]));
              addedScalar = true;
            }
          });
        }
        // Keep the branch a valid selection set even when it has no scalar children (yet).
        if (!addedScalar && !hasDescendant(model.paths, segments)) {
          model.paths.add(pathKey([...segments, TYPENAME]));
        }
      }
      emitChange();
    }

    function emitChange(): void {
      emit("update:modelValue", serialize(model.paths));
    }

    function applyHydrated(json: any): void {
      model.paths.clear();
      hydrate(json).forEach((path) => model.paths.add(path));
    }

    async function hydrateSelection(): Promise<void> {
      const raw = (props.modelValue as string) || "";
      if (raw && raw.trim()) {
        const result = await parseSelection(
          connectorId,
          revision,
          raw,
          rootTypeName
        );
        if (!result.ok) {
          emit("fallback");
          return;
        }
        applyHydrated(result.data);
      } else {
        const def = await fetchDefaultSelection(
          connectorId,
          revision,
          operation,
          actionField
        );
        if (def) {
          // Populate the model for display/tri-state only. Emitting here would clobber the saved
          // selection just because the panel mounted; control.data is written only on a real toggle.
          applyHydrated(def);
        }
      }
    }

    async function resetToDefault(): Promise<void> {
      // Don't race the initial hydration: both paths call applyHydrated() (clear + rebuild), so a
      // reset firing mid-hydration would leave model.paths in a non-deterministic state.
      if (hydrating.value) return;
      const def = await fetchDefaultSelection(
        connectorId,
        revision,
        operation,
        actionField
      );
      if (def) {
        applyHydrated(def);
        emitChange();
      }
    }

    function segmentOf(child: any): string {
      return child.kind === "FRAGMENT"
        ? fragmentSegment(child.onType)
        : fieldSegment(child.name);
    }

    // Muted one-line preview shown while collapsed, derived from the hydrated model (never emits).
    const collapsedSummary = computed(() => {
      if (hydrating.value) return "Loading…";
      const text = serialize(model.paths);
      return text.length > 90 ? `${text.slice(0, 89)}…` : text;
    });

    provide("selectionSetTree", {
      loadType,
      schemaFor,
      childrenMeta,
      stateOf,
      toggle,
    });

    onMounted(async () => {
      // Hydrate the selection model on mount (for display/tri-state and round-tripping). The root
      // type's fields are NOT loaded here — that is deferred to the "(all)" node's expand.
      hydrating.value = true;
      try {
        await hydrateSelection();
      } finally {
        hydrating.value = false;
      }
    });

    return {
      rootMeta,
      panelOpen,
      togglePanel,
      collapsedSummary,
      hydrating,
      resetToDefault,
    };
  },
});
